import { useCallback, useEffect, useState } from 'react'
import {
  billingApi,
  type BillingPlanDTO,
  type BillingSummaryDTO,
} from '@/shared/api'

/**
 * LGPD Block F1 — "Plano" panel (Settings → Plano).
 *
 * Provider-neutral and disabled-by-default. Shows the current plan/status and
 * available plans, with clear messaging when billing is not enabled/configured.
 * NO card fields are collected here — any real payment entry will redirect to a
 * provider checkout/portal (Block F2). Never shows a fake paid status.
 */

function errMessage(err: unknown): string {
  if (err && typeof err === 'object' && 'message' in err) {
    const m = (err as { message?: unknown }).message
    if (typeof m === 'string' && m.trim()) return m
  }
  return 'Não foi possível carregar as informações de cobrança.'
}

function formatPrice(plan: BillingPlanDTO): string {
  if (plan.price_cents == null) return '—'
  if (plan.price_cents === 0) return 'Grátis'
  const value = (plan.price_cents / 100).toLocaleString('pt-BR', {
    style: 'currency',
    currency: plan.currency || 'BRL',
  })
  const suffix =
    plan.billing_interval === 'monthly'
      ? '/mês'
      : plan.billing_interval === 'yearly'
        ? '/ano'
        : ''
  return `${value}${suffix}`
}

export function BillingSection() {
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [summary, setSummary] = useState<BillingSummaryDTO | null>(null)
  const [plans, setPlans] = useState<BillingPlanDTO[]>([])
  const [actionMsg, setActionMsg] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)

  const load = useCallback(async () => {
    setLoading(true)
    setError(null)
    try {
      const [me, list] = await Promise.all([
        billingApi.getMe(),
        billingApi.getPlans(),
      ])
      setSummary(me)
      setPlans(list.items)
    } catch (err) {
      setError(errMessage(err))
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    void load()
  }, [load])

  const onManage = async () => {
    if (busy) return
    setBusy(true)
    setActionMsg(null)
    try {
      const res = await billingApi.portal()
      if (res.enabled && res.portal_url) {
        window.location.assign(res.portal_url)
        return
      }
      setActionMsg(res.detail)
    } catch (err) {
      setActionMsg(errMessage(err))
    } finally {
      setBusy(false)
    }
  }

  const onSubscribe = async (planCode: string) => {
    if (busy) return
    setBusy(true)
    setActionMsg(null)
    try {
      const res = await billingApi.checkout(planCode)
      if (res.enabled && res.checkout_url) {
        window.location.assign(res.checkout_url)
        return
      }
      setActionMsg(res.detail)
    } catch (err) {
      setActionMsg(errMessage(err))
    } finally {
      setBusy(false)
    }
  }

  return (
    <div className="settings-section" data-testid="settings-billing">
      <header className="settings-section-head">
        <h3>Plano &amp; cobrança</h3>
        <p className="settings-section-help">
          Veja seu plano atual e os planos disponíveis. Nenhum dado de cartão é
          coletado aqui — pagamentos, quando habilitados, acontecem no checkout
          do provedor.
        </p>
      </header>

      {loading && <p className="settings-section-help">Carregando…</p>}
      {error && (
        <p className="settings-section-help" role="alert" data-testid="billing-error">
          {error}
        </p>
      )}

      {!loading && summary && (
        <>
          <div className="settings-card" data-testid="billing-current">
            <div>
              <strong>
                Plano atual: {summary.plan?.name ?? summary.entitlements.plan_code}
              </strong>
            </div>
            <div className="settings-meta">
              Status:{' '}
              {summary.subscription
                ? summary.subscription.status
                : 'sem assinatura ativa'}
            </div>
            {!summary.enabled && (
              <div className="settings-meta" data-testid="billing-disabled-note">
                Cobrança ainda não está habilitada nesta instância (beta). Você
                continua usando o Rager normalmente.
              </div>
            )}
          </div>

          <div className="settings-form-actions">
            <button
              type="button"
              className="settings-button"
              data-testid="billing-manage"
              onClick={() => void onManage()}
              disabled={busy || !summary.enabled}
              title={
                summary.enabled
                  ? 'Gerenciar assinatura'
                  : 'Disponível quando a cobrança for habilitada'
              }
            >
              Gerenciar assinatura
            </button>
          </div>

          {plans.length > 0 && (
            <ul className="settings-sharing-list" data-testid="billing-plans">
              {plans.map((plan) => (
                <li key={plan.code} className="settings-sharing-item">
                  <strong>{plan.name}</strong>{' '}
                  <span className="settings-meta">· {formatPrice(plan)}</span>
                  {plan.description && (
                    <div className="settings-meta">{plan.description}</div>
                  )}
                  <div className="settings-form-actions">
                    <button
                      type="button"
                      className="settings-button"
                      data-testid={`billing-subscribe-${plan.code}`}
                      onClick={() => void onSubscribe(plan.code)}
                      disabled={
                        busy ||
                        !summary.enabled ||
                        plan.code === summary.entitlements.plan_code
                      }
                    >
                      {plan.code === summary.entitlements.plan_code
                        ? 'Plano atual'
                        : 'Assinar'}
                    </button>
                  </div>
                </li>
              ))}
            </ul>
          )}

          {actionMsg && (
            <p className="settings-section-help" data-testid="billing-action-msg">
              {actionMsg}
            </p>
          )}
        </>
      )}
    </div>
  )
}
